import React from 'react';
import styled from 'styled-components';
import { colors, borderRadius } from '../styles/designTokens';
import { useNavigate } from 'react-router-dom';
import { Customer } from '../models/CustomerModels';
import { formatDateTimeKST } from '../utils/dateUtils';
import { Table, TableHeader, TableSection } from '../styles/DashboardStyles'; // 기존 스타일 활용

// DashboardCustomerTable Props 인터페이스
interface DashboardCustomerTableProps {
  customers: Customer[];
}

// 상세 정보 버튼 스타일
const DetailButton = styled.button`
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 500;
  color: ${colors.primary[500]};
  background-color: ${colors.primary[50]};
  border: 1px solid ${colors.primary[200]};
  border-radius: ${borderRadius.md};
  cursor: pointer;
  transition: all 0.2s ease-in-out;

  &:hover {
    background-color: ${colors.primary[100]};
    border-color: ${colors.primary[300]};
    color: ${colors.primary[600]};
  }
`;

// 테이블 스타일 약간 커스텀 (필요하다면)
const StyledTable = styled(Table)`
  th,
  td {
    // white-space: nowrap; // 내용이 길어지면 줄바꿈 방지 (필요시)
    // text-overflow: ellipsis; // 내용이 길어지면 ... 처리 (필요시)
    // overflow: hidden; // 내용이 길어지면 숨김 (필요시)
  }
  td:nth-child(1), th:nth-child(1) { width: 18%; } // 이름 또는 회사
  td:nth-child(2), th:nth-child(2) { width: 12%; } // 계정 ID
  td:nth-child(3), th:nth-child(3) { width: 18%; } // 이메일
  td:nth-child(4), th:nth-child(4) { width: 8%; text-align: right; } // 결제 금액
  td:nth-child(5), th:nth-child(5) { width: 8%; text-align: center; } // 전체 키워드
  td:nth-child(6), th:nth-child(6) { width: 8%; text-align: center; } // 활성 키워드
  td:nth-child(7), th:nth-child(7) { width: 13%; text-align: center; } // 마지막 로그인
  td:nth-child(8), th:nth-child(8) { width: 10%; text-align: center; } // 상세 정보
`;

const DashboardCustomerTable: React.FC<DashboardCustomerTableProps> = ({ customers }) => {
  const navigate = useNavigate(); // 라우터 네비게이션 훅 추가

  // 숫자를 "N,NNN원" 또는 "N천만원" 등으로 포맷하는 함수
  const formatCurrency = (amount: number): string => {
    if (amount === 0) return '0원';
    if (amount >= 10000000) { // 천만원 이상
      return `${Math.floor(amount / 10000000)}천만원`;
    }
    return `${amount.toLocaleString()}원`;
  };

  // 고객 상세 페이지로 이동하는 함수
  const handleDetailClick = (userIdx: number) => {
    navigate(`/customers/${userIdx}`);
  };

  return (
    <TableSection> {/* 테이블 전체를 감싸는 컨테이너 */}
      <TableHeader>
        <h3>고객 목록</h3>
      </TableHeader>
      <StyledTable>
        <thead>
          <tr>
            <th>이름 또는 회사</th>
            <th>계정 ID</th>
            <th>이메일</th>
            <th>결제 금액</th>
            <th>전체 키워드</th>
            <th>활성 키워드</th>
            <th>마지막 로그인</th>
            <th>상세 정보</th>
          </tr>
        </thead>
        <tbody>
          {customers.map(customer => (
            <tr key={customer.user_idx}>
              <td>{customer.user_name}</td>
              <td>{customer.user_id}</td>
              <td>{customer.user_email}</td>
              <td>{formatCurrency(customer.user_paid)}</td>
              <td>{customer.total_keywords.toLocaleString('ko-KR')}</td>
              <td>{customer.active_keywords.toLocaleString('ko-KR')}</td>
              <td>
                {customer.last_datetime ? formatDateTimeKST(customer.last_datetime) : 'N/A'}
              </td>
              <td>
                <DetailButton onClick={() => handleDetailClick(customer.user_idx)}>
                  보기
                </DetailButton>
              </td>
            </tr>
          ))}
          {(!customers || customers.length === 0) && (
            <tr>
              <td colSpan={8} style={{ textAlign: 'center', padding: '20px' }}>
                표시할 고객 데이터가 없습니다.
              </td>
            </tr>
          )}
        </tbody>
      </StyledTable>
    </TableSection>
  );
};

export default DashboardCustomerTable; 